import { useEffect, useState } from 'react';
import { apiClient } from '@/api/client';

interface Member {
  id: string;
  name: string;
  role: string;
  branchId: string;
  branchName?: string;
  active: boolean;
}

const ROLES = ['MANAGER', 'CASHIER', 'PRODUCTION', 'VIEWER'];

/**
 * Staff administration. Names are the sign-in identifier, so they are set at
 * creation and not editable here — renaming someone would change how they
 * log in and orphan their history.
 */
export default function StaffPanel({
  branchId,
  isManager,
}: {
  branchId: string;
  isManager: boolean;
}) {
  const [staff, setStaff] = useState<Member[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [resetting, setResetting] = useState<Member | null>(null);

  const [newStaff, setNewStaff] = useState({ name: '', pin: '', role: 'CASHIER', branchId: '' });

  const load = async () => {
    try {
      const [s, b] = await Promise.all([
        apiClient.listStaff(undefined, true),
        apiClient.getBranches().catch(() => ({ data: [] })),
      ]);
      setStaff(Array.isArray(s.data) ? s.data : []);
      setBranches(Array.isArray(b.data) ? b.data : []);
      setErr('');
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Could not load staff');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!newStaff.branchId && branchId) setNewStaff(s => ({ ...s, branchId }));
  }, [branchId, newStaff.branchId]);

  const flash = (m: string) => {
    setMsg(m);
    setErr('');
    setTimeout(() => setMsg(''), 4000);
  };

  const fail = (e: any, fallback: string) => {
    const d = e?.response?.data?.message;
    setErr(Array.isArray(d) ? d.join(', ') : d || fallback);
  };

  const add = async () => {
    try {
      await apiClient.createStaff(newStaff.name, newStaff.pin, newStaff.role, newStaff.branchId);
      flash(`${newStaff.name} added.`);
      setNewStaff({ name: '', pin: '', role: 'CASHIER', branchId });
      setShowAdd(false);
      await load();
    } catch (e) {
      fail(e, 'Could not add them');
    }
  };

  const setActive = async (m: Member, active: boolean) => {
    try {
      await apiClient.updateStaff(m.id, { active });
      flash(`${m.name} ${active ? 'reactivated' : 'deactivated'}.`);
      await load();
    } catch (e) {
      fail(e, 'Could not update');
    }
  };

  const setRole = async (m: Member, role: string) => {
    try {
      await apiClient.updateStaff(m.id, { role });
      flash(`${m.name} is now ${role.toLowerCase()}.`);
      await load();
    } catch (e) {
      fail(e, 'Could not change role');
    }
  };

  if (loading) return <div className="text-gray-500 py-8">Loading staff…</div>;

  const active = staff.filter(s => s.active);
  const inactive = staff.filter(s => !s.active);

  return (
    <div className="space-y-6">
      {msg && <div className="bg-green-50 text-green-800 rounded-lg px-4 py-3 text-sm font-medium">{msg}</div>}
      {err && <div className="bg-red-50 text-red-700 rounded-lg px-4 py-3 text-sm font-medium">{err}</div>}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Active staff</h3>
          <p className="mt-1 text-3xl font-bold text-gray-900 tabular-nums">{active.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Managers</h3>
          <p className="mt-1 text-3xl font-bold text-gray-900 tabular-nums">
            {active.filter(s => s.role === 'MANAGER').length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Deactivated</h3>
          <p className="mt-1 text-3xl font-bold text-gray-400 tabular-nums">{inactive.length}</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b flex justify-between items-center gap-3">
          <h3 className="text-lg font-medium text-gray-900">Staff</h3>
          {isManager && (
            <button
              onClick={() => setShowAdd(v => !v)}
              className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-4 py-2 rounded-lg text-sm"
            >
              {showAdd ? 'Cancel' : '+ Add staff'}
            </button>
          )}
        </div>

        {showAdd && (
          <div className="p-6 border-b bg-amber-50/40 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                value={newStaff.name}
                onChange={e => setNewStaff(s => ({ ...s, name: e.target.value }))}
                placeholder="They sign in with this"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">PIN (4–6 digits)</label>
              <input
                value={newStaff.pin}
                onChange={e => setNewStaff(s => ({ ...s, pin: e.target.value.replace(/\D/g, '') }))}
                maxLength={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg tabular-nums tracking-widest"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select
                value={newStaff.role}
                onChange={e => setNewStaff(s => ({ ...s, role: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
              >
                {ROLES.map(r => <option key={r} value={r}>{r[0] + r.slice(1).toLowerCase()}</option>)}
              </select>
            </div>
            <div className="flex gap-2">
              <select
                value={newStaff.branchId}
                onChange={e => setNewStaff(s => ({ ...s, branchId: e.target.value }))}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
              >
                {branches.map((b: any) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
              <button
                onClick={add}
                className="bg-amber-500 hover:bg-amber-600 text-white font-bold px-4 py-2 rounded-lg text-sm whitespace-nowrap"
              >
                Add
              </button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left py-2.5 px-6 text-xs font-semibold uppercase tracking-wide text-gray-600">Name</th>
                <th className="text-left py-2.5 px-4 text-xs font-semibold uppercase tracking-wide text-gray-600">Role</th>
                <th className="text-left py-2.5 px-4 text-xs font-semibold uppercase tracking-wide text-gray-600">Branch</th>
                <th className="text-right py-2.5 px-6 text-xs font-semibold uppercase tracking-wide text-gray-600">Status</th>
              </tr>
            </thead>
            <tbody>
              {staff.map(m => (
                <tr key={m.id} className={`border-b last:border-0 ${m.active ? '' : 'opacity-50'}`}>
                  <td className="py-2.5 px-6 font-medium text-gray-900">{m.name}</td>
                  <td className="py-2.5 px-4">
                    {isManager && m.active ? (
                      <select
                        value={m.role}
                        onChange={e => setRole(m, e.target.value)}
                        className="px-2 py-1 border border-gray-200 rounded text-xs bg-white"
                      >
                        {ROLES.map(r => <option key={r} value={r}>{r[0] + r.slice(1).toLowerCase()}</option>)}
                      </select>
                    ) : (
                      <span className="text-gray-600">{m.role[0] + m.role.slice(1).toLowerCase()}</span>
                    )}
                  </td>
                  <td className="py-2.5 px-4 text-gray-600">{m.branchName || '—'}</td>
                  <td className="py-2.5 px-6 text-right whitespace-nowrap">
                    <span
                      className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                        m.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {m.active ? 'active' : 'deactivated'}
                    </span>
                    {isManager && (
                      <>
                        <button
                          onClick={() => setResetting(m)}
                          className="ml-3 text-xs font-medium text-amber-700 hover:text-amber-900 underline"
                        >
                          Reset PIN
                        </button>
                        <button
                          onClick={() => setActive(m, !m.active)}
                          className="ml-3 text-xs font-medium text-gray-600 hover:text-gray-900 underline"
                        >
                          {m.active ? 'Deactivate' : 'Reactivate'}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ChangeOwnPin onDone={flash} onError={setErr} />

      {resetting && (
        <ResetPinModal
          member={resetting}
          onClose={() => setResetting(null)}
          onDone={(m) => { setResetting(null); flash(m); }}
        />
      )}
    </div>
  );
}

function ChangeOwnPin({ onDone, onError }: { onDone: (m: string) => void; onError: (m: string) => void }) {
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (newPin !== confirmPin) {
      onError('The new PIN and the confirmation do not match');
      return;
    }
    setBusy(true);
    try {
      await apiClient.changeOwnPin(currentPin, newPin);
      setCurrentPin(''); setNewPin(''); setConfirmPin('');
      onDone('Your PIN has been changed.');
    } catch (e: any) {
      const d = e?.response?.data?.message;
      onError(Array.isArray(d) ? d.join(', ') : d || 'Could not change your PIN');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b">
        <h3 className="text-lg font-medium text-gray-900">Change your own PIN</h3>
        <p className="text-sm text-gray-500">Everyone's PIN came across from the old system — worth changing.</p>
      </div>
      <div className="p-6 grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Current PIN</label>
          <input type="password" value={currentPin} onChange={e => setCurrentPin(e.target.value.replace(/\D/g, ''))}
            maxLength={6} className="w-full px-3 py-2 border border-gray-300 rounded-lg tracking-widest" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">New PIN</label>
          <input type="password" value={newPin} onChange={e => setNewPin(e.target.value.replace(/\D/g, ''))}
            maxLength={6} className="w-full px-3 py-2 border border-gray-300 rounded-lg tracking-widest" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Confirm new PIN</label>
          <input type="password" value={confirmPin} onChange={e => setConfirmPin(e.target.value.replace(/\D/g, ''))}
            maxLength={6} className="w-full px-3 py-2 border border-gray-300 rounded-lg tracking-widest" />
        </div>
        <button onClick={submit} disabled={busy}
          className="bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-bold px-5 py-2 rounded-lg">
          {busy ? 'Changing…' : 'Change PIN'}
        </button>
      </div>
    </div>
  );
}

function ResetPinModal({
  member, onClose, onDone,
}: { member: Member; onClose: () => void; onDone: (m: string) => void }) {
  const [newPin, setNewPin] = useState('');
  const [managerPin, setManagerPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    setBusy(true);
    try {
      const res = await apiClient.resetStaffPin(member.id, newPin, managerPin);
      onDone(res.data?.message || `PIN reset for ${member.name}`);
    } catch (e: any) {
      const d = e?.response?.data?.message;
      setErr(Array.isArray(d) ? d.join(', ') : d || 'Could not reset it');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <h3 className="text-lg font-bold text-gray-900">Reset PIN — {member.name}</h3>
        <p className="text-sm text-gray-500 mt-1">
          They will sign in with the new PIN immediately. Tell them what it is.
        </p>

        <label className="block text-sm font-medium text-gray-700 mt-5 mb-1">New PIN for {member.name}</label>
        <input value={newPin} onChange={e => setNewPin(e.target.value.replace(/\D/g, ''))} maxLength={6}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg tabular-nums tracking-widest text-lg" />

        <label className="block text-sm font-medium text-gray-700 mt-4 mb-1">Your own PIN, to authorise</label>
        <input type="password" value={managerPin} onChange={e => setManagerPin(e.target.value.replace(/\D/g, ''))}
          maxLength={6} className="w-full px-4 py-2 border border-gray-300 rounded-lg tracking-widest" />

        {err && <p className="text-sm text-red-600 mt-2">{err}</p>}

        <div className="flex gap-3 mt-5">
          <button onClick={submit} disabled={busy}
            className="flex-1 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-bold py-2.5 rounded-lg">
            {busy ? 'Resetting…' : 'Reset PIN'}
          </button>
          <button onClick={onClose} className="px-5 py-2.5 rounded-lg bg-gray-100 hover:bg-gray-200 font-medium text-gray-700">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
